"use client";
import clsx from "clsx";
import type { MouseEventHandler, ReactNode } from "react";

type Props = {
  as?: "button" | "span";
  id?: string;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  children: ReactNode;
  className?: string;
  type?: "button" | "submit" | "reset";
  disabled?: boolean;
  size?: "default" | "sm" | "icon";
  direction?: "up" | "right" | "down" | "left";
};
export const PrimaryButton = ({
  as = "button",
  id,
  onClick,
  children,
  className,
  type = "button",
  disabled,
  size = "default",
  direction = "right",
}: Props) => {
  const rootClassName = clsx(
    "group/btn btn-primary-gradient rounded-lg border border-pr-2 text-white stroke-white relative overflow-hidden disabled:grayscale cursor-pointer disabled:cursor-not-allowed inline-flex items-center justify-center",
    {
      "h-12 px-6 text-base/5 font-semibold": size === "default",
      "h-10 px-6 text-sm font-semibold": size === "sm",
      "h-10 w-10": size === "icon",
    },
    className,
  );

  const content = (
    <>
      <span className="absolute left-0 top-0 w-full h-[200%] btn-primary-gradient transition-transform group-hover/btn:-translate-y-1/2" />
      <span className="absolute -inset-px rounded-lg transition-shadow group-hover/btn:shadow-[0_-2.5px_0_0_rgba(120,40,3,0.24)_inset,0_2.5px_0_0_rgba(242,144,108,0.32)_inset]" />
      <span
        className={clsx(
          "flex justify-center items-center transition-transform group-hover/btn:drop-shadow-sm whitespace-nowrap",
          {
            "translate-x-0 group-hover/btn:translate-x-1": direction === "right",
            "translate-x-0 group-hover/btn:-translate-x-1": direction === "left",
            "translate-y-0 group-hover/btn:-translate-y-0.5": direction === "up",
            "translate-y-0 group-hover/btn:translate-y-0.5": direction === "down",
            "gap-2": size === "sm",
            "gap-4": size !== "sm",
          },
        )}
      >
        {children}
      </span>
    </>
  );

  if (as === "span") {
    return (
      <span id={id} className={rootClassName} aria-disabled={disabled}>
        {content}
      </span>
    );
  }

  return (
    <button id={id} type={type} className={rootClassName} onClick={onClick} disabled={disabled}>
      {content}
    </button>
  );
};
